import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  MessagingChannelReplyMode,
  MessagingChannelType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { MessagingChannel } from '~hooks/useMessaging/models';
import useStartConversation, {
  MessagingTargetConflictError,
} from '~hooks/useMessaging/useStartConversation';
import { ROUTES } from '~router/routePaths';
import { Nullable } from '~types/Nullable';

type StartConversationDialogProps = {
  channelName: string;
  channelType: MessagingChannelType.JOB | MessagingChannelType.ORDER;
  isOpen: boolean;
  onClose: () => void;
  targetId: string;
};

const JOB_COPY = {
  body: 'messaging.confirm_message_driver',
  targetConflict: 'error_messages.messaging.job_conversation_exists',
  title: 'messaging.message_driver',
};

const ORDER_COPY = {
  body: 'messaging.confirm_message_all_drivers',
  targetConflict: 'error_messages.messaging.order_conversation_exists',
  title: 'messaging.message_all_drivers',
};

/** The api 422s on a second job/order conversation, and a row cannot tell one exists. */
const StartConversationDialog = observer(
  ({
    channelName,
    channelType,
    isOpen,
    onClose,
    targetId,
  }: StartConversationDialogProps) => {
    const { startConversation } = useStartConversation();
    const navigate = useNavigate();
    const [isStarting, setIsStarting] = useState(false);
    const [existingChannel, setExistingChannel] =
      useState<Nullable<MessagingChannel>>(null);

    const isJobConversation = channelType === MessagingChannelType.JOB;
    const copy = isJobConversation ? JOB_COPY : ORDER_COPY;

    // The dialog outlives a close at some call sites, so it must not reopen on the conflict.
    const closeAndReset = useCallback(() => {
      setExistingChannel(null);
      onClose();
    }, [onClose]);

    const openExistingConversation = useCallback(() => {
      if (!existingChannel) return;

      closeAndReset();
      navigate(`/${ROUTES.messages}/${existingChannel.id}`);
    }, [closeAndReset, existingChannel, navigate]);

    const onConfirm = useCallback(async () => {
      setIsStarting(true);

      try {
        const channel = await startConversation({
          channelType,
          jobId: isJobConversation ? targetId : undefined,
          name: channelName,
          orderId: isJobConversation ? undefined : targetId,
          // An order conversation is one-to-many, so the api needs a reply mode.
          replyMode: isJobConversation ? undefined : MessagingChannelReplyMode.FORUM,
        });

        closeAndReset();
        navigate(`/${ROUTES.messages}/${channel.id}`);
      } catch (error) {
        if (error instanceof MessagingTargetConflictError) {
          setExistingChannel(error.existingChannel);
        }
        // Anything else is already toasted where it failed; the dialog stays open.
      } finally {
        setIsStarting(false);
      }
    }, [
      channelName,
      channelType,
      closeAndReset,
      isJobConversation,
      navigate,
      startConversation,
      targetId,
    ]);

    return (
      // A portalled dialog still bubbles through the React tree onto the card underneath.
      <Box onClick={(event) => event.stopPropagation()}>
        {existingChannel ? (
          <ConfirmationDialog
            confirmButtonProps={{
              children: t('messaging.open_conversation'),
              color: 'primary',
            }}
            isOpen={isOpen}
            onClose={closeAndReset}
            onConfirm={openExistingConversation}
            title={t(copy.title)}
          >
            <Typography>{t(copy.targetConflict)}</Typography>
          </ConfirmationDialog>
        ) : (
          <ConfirmationDialog
            confirmButtonProps={{
              children: t('messaging.start_conversation'),
              color: 'primary',
            }}
            isBusy={isStarting}
            isOpen={isOpen}
            onClose={closeAndReset}
            onConfirm={() => void onConfirm()}
            title={t(copy.title)}
          >
            <Typography>{t(copy.body, { name: channelName })}</Typography>
          </ConfirmationDialog>
        )}
      </Box>
    );
  },
);

export default StartConversationDialog;
